import deleteIcon from '@/assets/images/play/delete.svg';
import { ReactComponent as UploadIcon } from '@/assets/images/play/upload.svg';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
    AlertDialogTrigger,
} from '@/components/shadcn/alert-dialog';
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from '@/components/shadcn/tooltip';
import { Lazbubu } from '@/gql/graph/__generated__/request';
import { useDataAnchoringTokenContract } from '@/hooks/useContract';
import { useCreateMemoryPermit } from '@/hooks/useCreateMemoryPermit';
import { useLazbubu } from '@/hooks/useLazbubu';
import { useMemories } from '@/hooks/useMemories';
import { useMemoryAvailable } from '@/hooks/useMemoryAvailable';
import useSend from '@/hooks/useSend';
import Alert from '@/utils/components/alert';
import LinearProgress from '@mui/material/LinearProgress';
import moment from 'moment';
import { useEffect, useMemo, useState } from 'react';
import ReactMarkdown from 'react-markdown';

export default function Memory({ lazbubu }: { lazbubu: Lazbubu }) {
    const send = useSend();
    const dataAnchoringTokenContract = useDataAnchoringTokenContract();

    const { lazbubu: latestLazbubu, refetch: refetchLatestLazbubu } =
        useLazbubu(lazbubu.tokenId);

    const { memories, refetch: refetchMemories } = useMemories(lazbubu.tokenId);
    const { getCreateMemoryPermit } = useCreateMemoryPermit(lazbubu.tokenId);
    const { memoryAvailable, getMemoryAvailable } = useMemoryAvailable(
        lazbubu.tokenId,
    );

    const [uploadMemoryLoading, setUploadMemoryLoading] = useState(false);
    const [deleteMemoryLoading, setDeleteMemoryLoading] = useState(false);

    useEffect(() => {
        getMemoryAvailable();
        const interval = setInterval(() => {
            getMemoryAvailable();
        }, 10000);
        return () => clearInterval(interval);
    }, []);

    const uploadMemory = async () => {
        if (
            memoryAvailable &&
            dataAnchoringTokenContract &&
            !uploadMemoryLoading
        ) {
            setUploadMemoryLoading(true);
            const permit = await getCreateMemoryPermit();
            if (!permit?.data || !permit?.permit) {
                Alert({
                    type: 'error',
                    text: 'get permit failed. Please try again.',
                });
                return;
            }
            const permitData = JSON.parse(permit.data);
            const permitValue = JSON.parse(permit.permit);
            send({
                contract: dataAnchoringTokenContract,
                method: 'createMemory',
                params: [
                    lazbubu.tokenId,
                    permitData.contentHash,
                    [
                        Number(permitValue.permitType),
                        BigInt(permitValue.nonce),
                        permitValue.dataHash,
                        BigInt(permitValue.expire),
                        permitValue.sig,
                    ],
                ],
                onSuccess: () => {
                    refetchMemories();
                    refetchLatestLazbubu();
                    setUploadMemoryLoading(false);
                    getMemoryAvailable();
                },
                onError: (e) => {
                    setUploadMemoryLoading(false);
                    if (e?.code !== 'ACTION_REJECTED') {
                        Alert({
                            type: 'error',
                            text:
                                e?.shortMessage ||
                                e?.message ||
                                'Memory upload failed. Please try again.',
                        });
                    }
                },
            });
        }
    };

    const deleteMemory = async (id: bigint) => {
        if (deleteMemoryLoading) return;
        setDeleteMemoryLoading(true);
        send({
            contract: dataAnchoringTokenContract,
            method: 'deleteMemory',
            params: [lazbubu.tokenId, id],
            onSuccess: () => {
                refetchMemories();
                refetchLatestLazbubu();
                setDeleteMemoryLoading(false);
                getMemoryAvailable();
            },
            onError: (e) => {
                setDeleteMemoryLoading(false);
                if (e?.code !== 'ACTION_REJECTED') {
                    Alert({
                        type: 'error',
                        text:
                            e?.shortMessage ||
                            e?.message ||
                            'Memory deletion failed. Please try again.',
                    });
                }
            },
        });
    };

    const couldUploadMemory = useMemo(() => {
        return (
            memoryAvailable &&
            !uploadMemoryLoading &&
            latestLazbubu?.memoryUnitUsed !== latestLazbubu?.memoryUnitQuota
        );
    }, [memoryAvailable, uploadMemoryLoading, latestLazbubu]);

    return (
        <div className="w-full pb-[160px]">
            <div className="text-[20px] font-semibold max-md:text-[16px]">
                Current Memory Usage
            </div>
            <div className="flex items-center gap-[10px] mt-[24px]">
                <LinearProgress
                    variant="determinate"
                    value={
                        ((latestLazbubu?.memoryUnitUsed ?? 0) /
                            (latestLazbubu?.memoryUnitQuota ?? 1)) *
                        100
                    }
                    className="flex-1"
                />
                <div className="font-medium">
                    {latestLazbubu?.memoryUnitUsed ?? 0}/
                    {latestLazbubu?.memoryUnitQuota ?? 0} MU
                </div>
            </div>
            <div className="flex items-center justify-between mt-[40px]">
                <div className="text-[20px] font-semibold max-md:text-[16px]">
                    Memory Units (MU)
                </div>
                {memories.length > 0 && (
                    <TooltipProvider
                        delayDuration={!memoryAvailable ? 0 : 3000}
                    >
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <button
                                    className="text-[12px] flex max-md:text-[11px] max-md:px-[15px] max-md:py-[8px] items-center gap-[10px] disabled:text-gray disabled:cursor-not-allowed border-border bg-white px-[20px] py-[10px] rounded-[12px]"
                                    onClick={uploadMemory}
                                    disabled={!couldUploadMemory}
                                >
                                    <UploadIcon
                                        className={`w-[20px] h-[20px] max-md:w-[15px] max-md:h-[15px] ${
                                            !couldUploadMemory
                                                ? 'opacity-50'
                                                : ''
                                        }`}
                                    />
                                    {latestLazbubu?.memoryUnitUsed != null &&
                                    latestLazbubu?.memoryUnitUsed ===
                                        latestLazbubu?.memoryUnitQuota
                                        ? 'Memory is full'
                                        : 'Upload Memory'}
                                </button>
                            </TooltipTrigger>
                            <TooltipContent>
                                <p>
                                    Chat more with Lazbubu to create special
                                    memories together
                                </p>
                            </TooltipContent>
                        </Tooltip>
                    </TooltipProvider>
                )}
            </div>
            {memories.length > 0 ? (
                memories.map((memory) => (
                    <div
                        key={memory.id}
                        className="flex items-center gap-[20px] mt-[20px]"
                    >
                        <div className="flex-1 text-[12px] bg-white border border-border rounded-[16px] px-[20px] py-[10px]">
                            <div className="text-gray">
                                {moment(memory.timestamp * 1000).format(
                                    'MM/DD/YYYY hh:mm A',
                                )}
                            </div>
                            <div className="mt-[10px]">
                                <ReactMarkdown>{memory.content}</ReactMarkdown>
                            </div>
                        </div>
                        <AlertDialog>
                            <AlertDialogTrigger asChild>
                                <button className="border border-border flex items-center justify-center w-[32px] h-[24px] rounded-[8px]">
                                    <img src={deleteIcon} alt="delete" />
                                </button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                                <AlertDialogHeader>
                                    <AlertDialogTitle>
                                        Forget the memory
                                    </AlertDialogTitle>
                                    <AlertDialogDescription>
                                        Remove the memory? This cannot be
                                        undone.
                                    </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                    <AlertDialogCancel>
                                        Cancel
                                    </AlertDialogCancel>
                                    <AlertDialogAction
                                        onClick={() =>
                                            deleteMemory(BigInt(memory.id))
                                        }
                                    >
                                        Ok
                                    </AlertDialogAction>
                                </AlertDialogFooter>
                            </AlertDialogContent>
                        </AlertDialog>
                    </div>
                ))
            ) : (
                <div className="mt-[100px] flex flex-col items-center gap-[40px]">
                    <div className="text-[12px] text-center">
                        No Memory Yet.
                    </div>
                    <TooltipProvider
                        delayDuration={!memoryAvailable ? 0 : 3000}
                    >
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <button
                                    className="text-[12px] flex items-center gap-[10px] disabled:bg-gray disabled:cursor-not-allowed bg-black text-white px-[20px] py-[10px] rounded-[12px]"
                                    onClick={uploadMemory}
                                    disabled={!couldUploadMemory}
                                >
                                    {latestLazbubu?.memoryUnitUsed != null &&
                                    latestLazbubu?.memoryUnitUsed ===
                                        latestLazbubu?.memoryUnitQuota
                                        ? 'Memory is full'
                                        : 'Upload Memory'}
                                </button>
                            </TooltipTrigger>
                            <TooltipContent>
                                <p>
                                    Chat more with Lazbubu to create special
                                    memories together
                                </p>
                            </TooltipContent>
                        </Tooltip>
                    </TooltipProvider>
                </div>
            )}
        </div>
    );
}
